import React, { useState, useRef } from "react";
import { CheckCircle2 } from "lucide-react";
import { DayPicker } from "react-day-picker";
import "react-day-picker/dist/style.css";

import Header from "../components/Header";
import Badge from "../components/Badge";
import BlueButton from "../components/BlueButton";

// Custom date input with calendar popup
function DateInput({ selectedDate, setSelectedDate }) {
  const [showCalendar, setShowCalendar] = useState(false);
  const inputRef = useRef(null);

  const handleSelect = (date) => {
    if (!date) return;
    setSelectedDate(date);
    setShowCalendar(false);
  };

  return (
    <div className="relative">
      <input
        ref={inputRef}
        type="text"
        readOnly
        value={selectedDate ? selectedDate.toISOString().split("T")[0] : ""}
        onClick={() => setShowCalendar(!showCalendar)}
        className="w-full rounded-lg border px-3 py-2 text-sm cursor-pointer"
        placeholder="Select Payment Date"
      />
      {showCalendar && (
        <div className="absolute z-50 mt-1">
          <DayPicker
            mode="single"
            selected={selectedDate}
            onSelect={handleSelect}
            disabled={{ after: new Date() }} // restrict future dates
          />
        </div>
      )}
    </div>
  );
}

export default function Payments() {
  const [rows, setRows] = useState([
    {
      invoice: "INV-90021",
      retailer: "RT-1001",
      retailerName: "Maha Mobile Hub",
      key: "100",
      keyrate: "150",
      amount: 15000,
      pending: 4000,
      date: "2025-09-22",
      status: "Pending",
      remark: "",
    },
  ]);

  const [showModal, setShowModal] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [paidAmount, setPaidAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date());
  const [remark, setRemark] = useState("");

  const openModal = (row) => {
    setSelectedRow(row);
    setPaidAmount(row.pending); // prefill with full amount
    setPaymentDate(new Date());
    setRemark(row.remark || ""); // prefill if remark exists
    setShowModal(true);
  };

  const handleSubmit = () => {
    setRows((prev) =>
      prev.map((r) =>
        r.invoice === selectedRow.invoice
          ? {
              ...r,
              status: "Paid",
              amount: Number(paidAmount),
              date: paymentDate.toISOString().split("T")[0],
              remark: remark,
            }
          : r
      )
    );
    setShowModal(false);
  };

  return (
    <div className="space-y-3 sm:space-y-4">
      <Header title="Payments" />
      <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
        <table className="min-w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="px-4 py-3">Invoice</th>
              {/* <th className="px-4 py-3">Retailer (Code)</th> */}
              <th className="px-4 py-3">Key Details</th>
              <th className="px-4 py-3">Total</th>
              <th className="px-4 py-3">Pending</th>
              <th className="px-4 py-3">Date</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Remark</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.invoice} className="border-b last:border-0">
                <td className="px-4 py-3 font-medium">{r.invoice}</td>
                {/* <td className="px-4 py-3">
                  {r.retailerName} ({r.retailer})
                </td> */}
                <td className="px-4 py-3">
                  Keys: {r.key} Rate: ({r.keyrate})
                </td>
                <td className="px-4 py-3">
                  ₹ {r.amount.toLocaleString("en-IN")}
                </td>
                <td className="px-4 py-3">₹ {r.pending}</td>
                <td className="px-4 py-3">{r.date}</td>
                <td className="px-4 py-3">
                  <Badge
                    label={r.status}
                    color={
                      r.status === "Paid"
                        ? "blue"
                        : r.status === "Received"
                        ? "green"
                        : "red"
                    }
                  />
                </td>
                <td className="px-4 py-3">{r.remark || "-"}</td>
                <td className="px-4 py-3">
                  <BlueButton onClick={() => openModal(r)}>
                    <CheckCircle2 size={14} className="mr-1 inline" />
                    Mark as Paid
                  </BlueButton>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Payment Modal */}
      {showModal && selectedRow && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50">
          <div className="mt-20 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-[#0A3E91]">
                Mark as Paid - {selectedRow.retailerName} ({selectedRow.retailer})
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3">
              <input
                type="number"
                placeholder="Amount"
                value={paidAmount}
                onChange={(e) => setPaidAmount(e.target.value)}
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />

              <input
                type="date"
                value={paymentDate.toISOString().split("T")[0]}
                onChange={(e) => setPaymentDate(new Date(e.target.value))}
                max={new Date().toISOString().split("T")[0]}
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />

              {/* New Remark Input */}
              <textarea
                placeholder="Add Remark"
                value={remark}
                onChange={(e) => setRemark(e.target.value)}
                rows={3}
                className="w-full rounded-lg border px-3 py-2 text-sm"
              />

              <BlueButton onClick={handleSubmit} className="w-full">
                Submit
              </BlueButton>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// import React, { useState, useRef } from "react";
// import { CheckCircle2, XCircle } from "lucide-react";
// import { DayPicker } from "react-day-picker";
// import "react-day-picker/dist/style.css";

// import Header from "../components/Header";
// import Badge from "../components/Badge";
// import RedButton from "../components/RedButton";
// import BlueButton from "../components/BlueButton";

// // Custom date input with calendar popup
// function DateInput({ selectedDate, setSelectedDate }) {
//   const [showCalendar, setShowCalendar] = useState(false);
//   const inputRef = useRef(null);

//   const handleSelect = (date) => {
//     if (!date) return;
//     setSelectedDate(date);
//     setShowCalendar(false);
//   };

//   return (
//     <div className="relative">
//       <input
//         ref={inputRef}
//         type="text"
//         readOnly
//         value={selectedDate ? selectedDate.toISOString().split("T")[0] : ""}
//         onClick={() => setShowCalendar(!showCalendar)}
//         className="w-full rounded-lg border px-3 py-2 text-sm cursor-pointer"
//         placeholder="Select Payment Date"
//       />
//       {showCalendar && (
//         <div className="absolute z-50 mt-1">
//           <DayPicker
//             mode="single"
//             selected={selectedDate}
//             onSelect={handleSelect}
//             disabled={{ after: new Date() }} // restrict future dates
//           />
//         </div>
//       )}
//     </div>
//   );
// }

// export default function Payments() {
//   const [rows, setRows] = useState([
//     { invoice: "INV-90021", retailer: "RT-1001", retailerName: "Maha Mobile Hub", key: "100", keyrate: "150", amount: 15000, pending: 4000, date: "2025-09-22", status: "Pending" },
//   ]);

//   const [showModal, setShowModal] = useState(false);
//   const [selectedRow, setSelectedRow] = useState(null);
//   const [paidAmount, setPaidAmount] = useState("");
//   const [paymentDate, setPaymentDate] = useState(new Date());

//   const openModal = (row) => {
//     setSelectedRow(row);
//     setPaidAmount(row.pending); // prefill with full amount
//     setPaymentDate(new Date());
//     setShowModal(true);
//   };

//   const handleSubmit = () => {
//     setRows((prev) =>
//       prev.map((r) =>
//         r.invoice === selectedRow.invoice
//           ? { ...r, status: "Paid", amount: Number(paidAmount), date: paymentDate.toISOString().split("T")[0] }
//           : r
//       )
//     );
//     setShowModal(false);
//   };

//   const markPending = (invoice) => {
//     setRows((prev) =>
//       prev.map((r) => (r.invoice === invoice ? { ...r, status: "Pending" } : r))
//     );
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Payments" />
//       <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
//         <table className="min-w-full text-sm">
//           <thead className="bg-slate-50">
//             <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
//               <th className="px-4 py-3">Invoice</th>
//               <th className="px-4 py-3">Retailer (Code)</th>
//               <th className="px-4 py-3">Key Details</th>
//               <th className="px-4 py-3">Total</th>
//               <th className="px-4 py-3">Pending</th>
//               <th className="px-4 py-3">Date</th>
//               <th className="px-4 py-3">Status</th>
//               <th className="px-4 py-3">Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {rows.map((r) => (
//               <tr key={r.invoice} className="border-b last:border-0">
//                 <td className="px-4 py-3 font-medium">{r.invoice}</td>
//                 <td className="px-4 py-3">{r.retailerName} ({r.retailer})</td>
//                 <td className="px-4 py-3">Keys: {r.key} Rate: ({r.keyrate})</td>
//                 <td className="px-4 py-3">₹ {r.amount.toLocaleString("en-IN")}</td>
//                 <td className="px-4 py-3">₹ {r.pending}</td>
//                 <td className="px-4 py-3">{r.date}</td>
//                 <td className="px-4 py-3">
//                   <Badge
//                     label={r.status}
//                     color={
//                       r.status === "Paid"
//                         ? "blue"
//                         : r.status === "Received"
//                         ? "green"
//                         : "red"
//                     }
//                   />
//                 </td>
//                 <td className="px-4 py-3">
//                   <div className="flex flex-wrap gap-2">
//                     <BlueButton onClick={() => openModal(r)}>
//                       <CheckCircle2 size={14} className="mr-1 inline" />Mark as Paid
//                     </BlueButton>
//                     {/* <RedButton onClick={() => markPending(r.invoice)}>
//                       <XCircle size={14} className="mr-1 inline" />Mark Pending
//                     </RedButton> */}
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Payment Modal */}
//       {/* {showModal && selectedRow && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Mark as Paid - {selectedRow.retailerName} ({selectedRow.retailer})
//               </h3>
//               <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
//             </div>

//             <div className="space-y-3">
//               <input
//                 type="number"
//                 placeholder="Amount"
//                 value={paidAmount}
//                 onChange={(e) => setPaidAmount(e.target.value)}
//                 className="w-full rounded-lg border px-3 py-2 text-sm"
//               />

//               <DateInput selectedDate={paymentDate} setSelectedDate={setPaymentDate} />

//               <BlueButton onClick={handleSubmit} className="w-full">Submit</BlueButton>
//             </div>
//           </div>
//         </div>
//       )} */}

//       {/* Payment Modal */}
//       {showModal && selectedRow && (
//         <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50">
//           <div className="mt-20 w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Mark as Paid - {selectedRow.retailerName} ({selectedRow.retailer})
//               </h3>
//               <button
//                 onClick={() => setShowModal(false)}
//                 className="text-gray-500 hover:text-gray-700"
//               >
//                 ✕
//               </button>
//             </div>

//             <div className="space-y-3">
//               <input
//                 type="number"
//                 placeholder="Amount"
//                 value={paidAmount}
//                 onChange={(e) => setPaidAmount(e.target.value)}
//                 className="w-full rounded-lg border px-3 py-2 text-sm"
//               />

//               <input
//                 type="date"
//                 value={paymentDate.toISOString().split("T")[0]}
//                 onChange={(e) => setPaymentDate(new Date(e.target.value))}
//                 max={new Date().toISOString().split("T")[0]}
//                 className="w-full rounded-lg border px-3 py-2 text-sm"
//               />

//               <BlueButton onClick={handleSubmit} className="w-full">
//                 Submit
//               </BlueButton>
//             </div>
//           </div>
//         </div>
//       )}


//     </div>
//   );
// }


// import React, { useState } from "react";
// import { CheckCircle2, XCircle } from "lucide-react";
// import { DayPicker } from "react-day-picker";
// import "react-day-picker/dist/style.css";

// import Header from "../components/Header";
// import Badge from "../components/Badge";
// import RedButton from "../components/RedButton";
// import BlueButton from "../components/BlueButton";

// export default function Payments() {
//   const [rows, setRows] = useState([
//     { invoice: "INV-90021", retailer: "RT-1001", retailerName: "Maha Mobile Hub", amount: 52400, date: "2025-09-22", status: "Pending" },
//     { invoice: "INV-90022", retailer: "RT-1002", retailerName: "Ganesh Telecom", amount: 34990, date: "2025-09-21", status: "Received" },
//   ]);

//   const [showModal, setShowModal] = useState(false);
//   const [selectedRow, setSelectedRow] = useState(null);
//   const [paidAmount, setPaidAmount] = useState("");
//   const [paymentDate, setPaymentDate] = useState(new Date());

//   const openModal = (row) => {
//     setSelectedRow(row);
//     setPaidAmount(row.amount); // prefill with full amount
//     setPaymentDate(new Date());
//     setShowModal(true);
//   };

//   const handleSubmit = () => {
//     setRows((prev) =>
//       prev.map((r) =>
//         r.invoice === selectedRow.invoice
//           ? { ...r, status: "Paid", amount: Number(paidAmount), date: paymentDate.toISOString().split("T")[0] }
//           : r
//       )
//     );
//     setShowModal(false);
//   };

//   const markPending = (invoice) => {
//     setRows((prev) =>
//       prev.map((r) => (r.invoice === invoice ? { ...r, status: "Pending" } : r))
//     );
//   };

//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Payments" />
//       <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
//         <table className="min-w-full text-sm">
//           <thead className="bg-slate-50">
//             <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
//               <th className="px-4 py-3">Invoice</th>
//               <th className="px-4 py-3">Retailer (Code)</th>
//               <th className="px-4 py-3">Amount</th>
//               <th className="px-4 py-3">Date</th>
//               <th className="px-4 py-3">Status</th>
//               <th className="px-4 py-3">Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {rows.map((r) => (
//               <tr key={r.invoice} className="border-b last:border-0">
//                 <td className="px-4 py-3 font-medium">{r.invoice}</td>
//                 <td className="px-4 py-3">{r.retailerName} ({r.retailer})</td>
//                 <td className="px-4 py-3">₹ {r.amount.toLocaleString("en-IN")}</td>
//                 <td className="px-4 py-3">{r.date}</td>
//                 <td className="px-4 py-3">{Badge(r.status, r.status === "Paid" ? "blue" : r.status === "Received" ? "green" : "red")}</td>
//                 <td className="px-4 py-3">
//                   <div className="flex flex-wrap gap-2">
//                     <BlueButton onClick={() => openModal(r)}>
//                       <CheckCircle2 size={14} className="mr-1 inline" />Mark as Paid
//                     </BlueButton>
//                     <RedButton onClick={() => markPending(r.invoice)}>
//                       <XCircle size={14} className="mr-1 inline" />Mark Pending
//                     </RedButton>
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>

//       {/* Payment Modal */}
//       {showModal && selectedRow && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
//             <div className="flex items-center justify-between mb-4">
//               <h3 className="text-lg font-semibold text-[#0A3E91]">
//                 Mark as Paid - {selectedRow.retailerName} ({selectedRow.retailer})
//               </h3>
//               <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-700">✕</button>
//             </div>

//             <div className="space-y-3">
//               <input
//                 type="number"
//                 placeholder="Amount"
//                 value={paidAmount}
//                 onChange={(e) => setPaidAmount(e.target.value)}
//                 className="w-full rounded-lg border px-3 py-2 text-sm"
//               />

//               <DayPicker
//                 mode="single"
//                 selected={paymentDate}
//                 onSelect={(date) => date && setPaymentDate(date)}
//                 disabled={{ after: new Date() }} // restrict to today or past
//               />

//               <BlueButton onClick={handleSubmit} className="w-full">Submit</BlueButton>
//             </div>
//           </div>
//         </div>
//       )}
//     </div>
//   );
// }


// import React, { useState } from "react";
// import { CheckCircle2, XCircle } from "lucide-react";
// import Header from "../components/Header";
// import Badge from "../components/Badge";
// import RedButton from "../components/RedButton";
// import BlueButton from "../components/BlueButton";

// export default function Payments() {
//   const [rows, setRows] = useState([
//     { invoice: "INV-90021", retailer: "RT-1001", retailerName: "Maha Mobile Hub", amount: 52400, date: "2025-09-22", status: "Pending" },
//     { invoice: "INV-90022", retailer: "RT-1002", retailerName: "Ganesh Telecom", amount: 34990, date: "2025-09-21", status: "Received" },
//   ]);
//   const updateStatus = (i, s) =>
//     setRows((prev) => prev.map((r, idx) => (idx === i ? { ...r, status: s } : r)));
//   return (
//     <div className="space-y-3 sm:space-y-4">
//       <Header title="Payments" />
//       <div className="overflow-x-auto rounded-2xl border bg-white shadow-sm">
//         <table className="min-w-full text-sm">
//           <thead className="bg-slate-50">
//             <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
//               <th className="px-4 py-3">Invoice</th>
//               <th className="px-4 py-3">Retailer (Code)</th>
//               <th className="px-4 py-3">Amount</th>
//               <th className="px-4 py-3">Date</th>
//               <th className="px-4 py-3">Status</th>
//               <th className="px-4 py-3">Action</th>
//             </tr>
//           </thead>
//           <tbody>
//             {rows.map((r, i) => (
//               <tr key={r.invoice} className="border-b last:border-0">
//                 <td className="px-4 py-3 font-medium">{r.invoice}</td>
//                 <td className="px-4 py-3">{r.retailerName} ({r.retailer})</td>
//                 <td className="px-4 py-3">₹ {r.amount.toLocaleString("en-IN")}</td>
//                 <td className="px-4 py-3">{r.date}</td>
//                 <td className="px-4 py-3">{Badge(r.status, r.status === "Received" ? "blue" : "red")}</td>
//                 <td className="px-4 py-3">
//                   <div className="flex flex-wrap gap-2">
//                     <BlueButton onClick={() => updateStatus(i, "Received")}>
//                       <CheckCircle2 size={14} className="mr-1 inline" />Mark Received
//                     </BlueButton>
//                     <RedButton onClick={() => updateStatus(i, "Pending")}>
//                       <XCircle size={14} className="mr-1 inline" />Mark Pending
//                     </RedButton>
//                   </div>
//                 </td>
//               </tr>
//             ))}
//           </tbody>
//         </table>
//       </div>
//     </div>
//   );
// }
